import type {
  GridApi,
  IsFullWidthRowParams,
  RowHeightParams,
} from "ag-grid-community";
import { ref } from "vue";

interface DetailRowLike {
  _isDetail: true;
  parentRowKey: string;
  skus: unknown[];
}

interface ParentRowLike {
  _isDetail?: false;
  rowKey: string;
}

type GridRowLike = ParentRowLike | DetailRowLike;

// Matches the fixed header/chrome offset AppGrid.vue's resizeGrid() adds on
// top of its data rows when sizing a detail grid - keep these in sync or the
// full-width detail row will overlap the row rendered after it.
const DETAIL_GRID_HEADER_CHROME_HEIGHT = 42;

interface GridRowManagementConfig {
  rowHeight: number;
  detailRowHeight: number;
  maxDetailVisibleRows: number;
  detailToggleColumnId?: string;
  detailHeaderHeight?: number;
}

export function useGridRowManagement<TGridRow extends GridRowLike>(
  config: GridRowManagementConfig
) {
  const expandedPOs = ref<Set<string>>(new Set());
  const gridApi = ref<GridApi<TGridRow> | undefined>(undefined);

  function resetExpandedRows(): void {
    expandedPOs.value = new Set();
  }

  function isFullWidthRow(parameters: IsFullWidthRowParams<TGridRow>): boolean {
    return (
      !!parameters.rowNode.data &&
      (parameters.rowNode.data as DetailRowLike)._isDetail
    );
  }

  function getRowId(parameters: { data: unknown }): string {
    const data = parameters.data as TGridRow;
    if ("_isDetail" in data && data._isDetail) {
      return `detail-${data.parentRowKey}`;
    }
    return data.rowKey;
  }

  function getRowHeight(parameters: RowHeightParams): number {
    const data = parameters.data as TGridRow | undefined;
    if (data && "_isDetail" in data && data._isDetail) {
      const visibleSkuRows = Math.min(
        data.skus.length,
        config.maxDetailVisibleRows
      );
      const headerHeight =
        config.detailHeaderHeight ?? DETAIL_GRID_HEADER_CHROME_HEIGHT;
      return config.detailRowHeight * visibleSkuRows + headerHeight;
    }
    return config.rowHeight;
  }

  function toggleDetail(rowKey: string): void {
    const expanded = new Set(expandedPOs.value);
    if (expanded.has(rowKey)) {
      expanded.delete(rowKey);
    } else {
      expanded.add(rowKey);
    }
    expandedPOs.value = expanded;

    const detailColumnId = config.detailToggleColumnId ?? "details";
    gridApi.value?.resetRowHeights();
    gridApi.value?.refreshCells({ columns: [detailColumnId], force: true });
  }

  return {
    expandedPOs,
    gridApi,
    resetExpandedRows,
    isFullWidthRow,
    getRowId,
    getRowHeight,
    toggleDetail,
  };
}
